import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrainFront, Lock, Mail, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { formatError } from "@/lib/api";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("admin@trams.local");
  const [password, setPassword] = useState("Admin@123");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome to TRAMS");
      navigate("/");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#f8fafc]" data-testid="login-page">
      {/* Left side branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e40af 100%)" }}>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, #3b82f6 0%, transparent 40%), radial-gradient(circle at 80% 70%, #93c5fd 0%, transparent 40%)"
        }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
              <TrainFront className="w-7 h-7" />
            </div>
            <div>
              <div className="text-2xl font-black tracking-tight">TRAMS</div>
              <div className="text-xs text-white/70">RAMS Dashboard</div>
            </div>
          </div>
          <div>
            <h1 className="text-4xl font-black leading-tight mb-4">
              RAMS Operational<br/>
              <span className="text-[#93c5fd]">Dashboard</span>
            </h1>
            <p className="text-white/70 max-w-md">
              Real-time monitoring of Reliability, Availability, Maintainability & Safety performance across the MRT network.
            </p>
          </div>
          <div className="text-xs text-white/50">
            © 2026 TRAMS Operations Control Center
          </div>
        </div>
      </div>

      {/* Right side form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#1e40af] flex items-center justify-center text-white">
              <TrainFront className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xl font-black text-[#1e40af]">TRAMS</div>
              <div className="text-xs text-slate-500">RAMS Dashboard</div>
            </div>
          </div>

          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Sign in</h2>
          <p className="text-sm text-slate-500 mt-2 mb-8">Access the RAMS operational dashboard.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11"
                  placeholder="admin@trams.local"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="login-toggle-password"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={submitting}
              className="w-full h-11 bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-semibold shadow-sm"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 p-4 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-1">
            <div className="font-semibold text-slate-700">Demo credentials</div>
            <div><span className="font-mono">admin@trams.local</span> / <span className="font-mono">Admin@123</span> (admin)</div>
            <div><span className="font-mono">viewer@trams.local</span> / <span className="font-mono">Viewer@123</span> (viewer)</div>
          </div>
        </div>
      </div>
    </div>
  );
}
